import React, { useContext, useState } from 'react';
import { View, Pressable, Switch } from 'react-native';
import styled from 'styled-components/native';

import ClientContext from 'app/src/contexts/ClientContext';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { useFormSubmit } from 'app/src/elements/forms';
import Modal from 'app/src/elements/Modal';
import { Notice } from 'app/src/elements/Notice';
import { Picker } from 'app/src/elements/Picker';
import { Subtitle, Text } from 'app/src/styles';
import colors from 'app/src/styles/colors';

import { UPDATE_USER } from './queries';

const ClientRow = styled(View)`
  flex-direction: row;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;
`;

const RemoveButton = styled(Pressable)`
  border-width: 1px;
  padding: 8px 12px;
  width: fit-content;
  border-style: solid;
  border-color: ${colors.error};
  border-radius: 4px;
  margin-top: 12px;
`;

const EditUser = ({ user, setShow, refetch }) => {
  const { currentUser } = useContext(ClientContext);

  const [clientUsers, setUserClients] = useState(
    user.clientUsers.map(userClient => ({
      clientId: userClient.client.id,
      role: userClient.admin ? 'admin' : 'normal',
    }))
  );

  const { submit: updateUser, formStatus, errorMessage } =
    useFormSubmit(UPDATE_USER, {
      onCompleted: () => {
        refetch();
        setShow(false);
      }
    });

  if (!currentUser) { return <Loading />; }

  const getRole = client => (
    clientUsers.find(entry => entry.clientId === client.id)?.role
  );

  const setRole = (client, role) => {
    const userClient = clientUsers.find(entry => entry.clientId === client.id);

    if (userClient) {
      userClient.role = role;
      setUserClients([ ...clientUsers ]);
    } else {
      setUserClients([ ...clientUsers, { clientId: client.id, role } ]);
    }
  };

  const clearRoles = () => {
    setUserClients(
      clientUsers.map(userClient => ({ ...userClient, role: '' }))
    );
  };

  const handleSubmit = () => {
    updateUser({
      variables: {
        input: {
          id: user.id,
          clientUsersAttributes: clientUsers,
        }
      }
    });
  };

  const clientsCanAdmin = currentUser.clientUsers
    .filter(userClient => userClient.admin)
    .map(userClient => userClient.client)
    .sort((client, otherClient) => client.name < otherClient.name ? -1 : 1);

  return (
    <Modal title="Edit User" setShow={setShow}>
      <View style={{ marginBottom: 10 }}>
        <Subtitle>{user.name}</Subtitle>
        <Text>{user.email}</Text>
      </View>

      <View style={{ marginBottom: 10 }}>
        <Subtitle>Client Access</Subtitle>
        <Text>
          Note: Users with an Administrator role can edit access for other
          users for that client.
        </Text>

        <RemoveButton onPress={clearRoles}>
          <Text>Remove All Access to Clients I administer</Text>
        </RemoveButton>
      </View>

      {clientsCanAdmin.map(client => (
        <ClientRow key={client.id}>
          <Switch
            trackColor={{ false: colors.switchTrack, true: colors.switchTrackActive }}
            thumbColor={getRole(client) ? colors.switchThumbActive : colors.switchThumb}
            activeTrackColor={colors.switchTrackActive}
            activeThumbColor={colors.switchThumbActive}
            value={!!getRole(client)}
            onValueChange={value => setRole(client, value ? 'normal' : '')}
          />

          <Text>{client.name}</Text>

          {getRole(client) ? (
            <Picker
              selectedValue={getRole(client)}
              onValueChange={value => setRole(client, value)}
            >
              <Picker.Item value="admin" label="Administrator" />
              <Picker.Item value="normal" label="Normal User" />
            </Picker>
          ) : (
            null
          )}
        </ClientRow>
      ))}

      {errorMessage && <Notice type="error">{errorMessage}</Notice>}

      {'submitting' === formStatus ? (
        <Loading />
      ) : (
        <Button label="Save" onPress={handleSubmit} wide />
      )}
    </Modal>
  );
};

export default EditUser;
